import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { sfx } from '../habitat/sfx';

// Re-exported so individual sheets can reach Dialog.Title/Dialog.Description/Dialog.Close
// without importing @radix-ui/react-dialog a second time — see this file's own header comment:
// every sheet in this app is built on THIS component, never directly on the Radix package, so
// this is the one place that import lives.
export { Dialog };

export type BottomSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Extra class(es) appended to Dialog.Content, alongside the shared `.sheet` class — lets each
   *  sheet keep its own modifier class (`.dye-sheet`, `.save-look-sheet`, ...) for any
   *  sheet-specific CSS. */
  contentClassName?: string;
  /** Extra class(es) on Dialog.Overlay (the dim). Overlay and Content are siblings inside the
   *  portal, so a sheet that animates its own card cannot select the dim with a descendant
   *  selector — it passes this instead (Today's open/close pop). */
  overlayClassName?: string;
  /**
   * Where focus goes when this sheet closes (Escape, overlay click, its own Close button, or the
   * swipe-dismiss gesture below) — typically a ref on whatever button opened it.
   *
   * Radix's own default behavior (restore focus to `document.activeElement` as captured when
   * `Dialog.Content` mounted) only reaches the *actual* trigger reliably when that trigger is a
   * `Dialog.Trigger` living inside the same `Dialog.Root` — `@radix-ui/react-dialog`'s own
   * source (`context.triggerRef.current?.focus()` in its `onCloseAutoFocus`) special-cases
   * exactly that path. Every sheet in this app is opened from a button in a *different*
   * component (e.g. `WardrobeScreen` opens `DyeSheet`), driven by external `open`/`onOpenChange`
   * state rather than a co-located `Dialog.Trigger`, so that special case never applies — Radix
   * falls back to `FocusScope`'s generic "restore `document.activeElement` as of mount" path,
   * which this codebase found empirically unreliable under jsdom for exactly this externally-
   * controlled shape (verified: a `Dialog.Trigger`-based dialog restores focus correctly in the
   * same test environment; an externally-controlled one does not — see docs/RULINGS.md's
   * "Wardrobe screen II — Radix migration" entry for the full investigation). `restoreFocusRef`
   * sidesteps the question entirely: `onCloseAutoFocus`'s `event.preventDefault()` skips Radix's
   * own restore attempt, and this component takes over with a direct, explicit `.focus()` call —
   * correct and deterministic regardless of environment, not just a test workaround.
   */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Confines the Radix Portal to a specific DOM node instead of the default `document.body`.
   * ONLY used by the component gallery (`gallery-entries.tsx`), which renders every state of
   * every component on one static page — without this, a `position: fixed` sheet portaled to
   * `document.body` would float over the ENTIRE gallery page instead of staying inside its own
   * figure. Real screens never pass this (`undefined` → Radix's own `document.body` default).
   */
  portalContainer?: HTMLElement | null;
  /**
   * `sheet` (default) is the bottom drawer. `overlay` is the Bright Acorns map-sheet:
   * a centered card over a dimmed scene. Habitat dock menus use overlay so the den
   * stays visible behind the panel.
   */
  variant?: 'sheet' | 'overlay';
  /**
   * When true, overlay click and Escape do not close — the parent owns dismiss
   * (habitat Dress: wardrobe Close has an unsaved-Look guard Radix cannot see).
   */
  lockDismiss?: boolean;
  /**
   * With `lockDismiss`, Escape calls this instead of doing nothing — the parent's own guarded
   * close path (QA 2026-09-23 onboard F24: the Dress overlay ignored Escape while the Equip
   * sheet beside it closed). Routed through Radix's own escape hook, so it fires only while this
   * sheet is the topmost layer — a sheet stacked on top takes its own Escape first.
   */
  onLockedEscape?: () => void;
};

// Swipe-down-to-dismiss threshold (vertical pointer travel on the drag handle) — an ADDITIONAL
// shortcut on top of Radix's own Escape/overlay-click/Dialog.Close dismissal (§11.3: "gestures
// are shortcuts, never the only path" — none of those other paths depend on this at all).
// raw-ok: gesture heuristic threshold, not a design token.
const SWIPE_DISMISS_PX = 40;

/**
 * The one shared bottom-sheet primitive for every overlay dialog in this app
 * (FABLE5_BUILD_PROMPT.md §3: "Headless primitives (Radix or equivalent) for dialogs, sheets,
 * menus — never hand-rolled."). Wraps `@radix-ui/react-dialog`'s Root/Portal/Overlay/Content:
 * Radix supplies the focus trap (Tab cycles within Content while open), Escape-to-close, and
 * overlay-click-to-close — all for free. This component adds exactly two things on top:
 * the drag-handle swipe-dismiss gesture (a shortcut, never the only path — see above), and the
 * `restoreFocusRef`/`portalContainer` escape hatches documented on their own props above.
 *
 * Every sheet in this app (`DyeSheet`, `SaveLookSheet`, `UnsavedChangesSheet`, and any future
 * one) MUST be built on this component, never directly on `@radix-ui/react-dialog` — see
 * docs/RULINGS.md's "Wardrobe screen II — Radix migration" entry. Individual sheets still reach
 * `Dialog.Title`/`Dialog.Description`/`Dialog.Close` themselves (re-exported as `Dialog` above)
 * for their own header/body markup, which varies too much between sheets to centralize here.
 */
export function BottomSheet({
  open, onOpenChange, children, contentClassName, overlayClassName, restoreFocusRef, portalContainer,
  variant = 'sheet',
  lockDismiss = false,
  onLockedEscape,
}: BottomSheetProps) {
  const dragStartY = useRef<number | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) sfx.sheetOpen();
    wasOpen.current = open;
  }, [open]);

  function handleHandlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    dragStartY.current = e.clientY;
  }
  function handleHandlePointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const start = dragStartY.current;
    dragStartY.current = null;
    if (start != null && e.clientY - start > SWIPE_DISMISS_PX) onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal container={portalContainer}>
        <Dialog.Overlay className={['sheet-backdrop', overlayClassName].filter(Boolean).join(' ')} />
        <Dialog.Content
          className={[
            'sheet',
            variant === 'overlay' ? 'sheet-overlay' : undefined,
            contentClassName,
          ].filter(Boolean).join(' ')}
          onCloseAutoFocus={
            restoreFocusRef
              ? (e) => { e.preventDefault(); restoreFocusRef.current?.focus(); }
              : undefined
          }
          onPointerDownOutside={lockDismiss ? (e) => e.preventDefault() : undefined}
          onInteractOutside={lockDismiss ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={lockDismiss ? (e) => { e.preventDefault(); onLockedEscape?.(); } : undefined}
        >
          {variant === 'sheet' && (
            <div
              className="sheet-drag-handle"
              aria-hidden="true"
              onPointerDown={handleHandlePointerDown}
              onPointerUp={handleHandlePointerUp}
            />
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
